clickDialogDirective.$inject = [];

/**
* @ngdoc  overview
* @module csm.ui
* @name   csm.ui.type:ClickDialogDirective
* 
@description
Creates an attribute directive that adds a **`click`** handler to the element
that will open an Angular-Bootstrap [**`$uibModal`**][1] dialog and bind the 
directive scope properties to the dialog controller.

When the directive element is clicked, the HTML "**`disabled`**" attribute is 
added the element and subsequent clicks will be ignored until the dialog is
closed and the attribute is removed. The element can be styled based on the
"**`disabled`**" attribute.

A **preloader** function or expression may be given and will be called before 
opening the dialog. Any data returned will be injected to the controller as 
`$preloaded`. If a promise is returned, the dialog will not open if the promise 
is rejected. 

# Usage

<pre class="prettyprint">
angular.module('MyApp', ['csm.ui']).directive("myDialog", function(ClickDialogDirective)
  {
  return new ClickDialogDirective(options);
  });
</pre>

### where

 - **`options`** = Directive options and [**`$uibModal.open()`**][1] options.

### Directive Options

- **`scope`**  
  Directive scope bindings. Defaults to a copy of the parent scope.  
  **Note:** two-way (=) bindings are not supported.
    
- **`preloader`**  
  Function reference or the name of a `scope` property containing a function 
  reference (<) or an expression (&). The function/expression is evaluated
  before opening the dialog and any data returned is injected to the controller
  as **`$preloaded`**. If a promise is returned, the dialog will not open if the 
  promise is rejected.
  
  **`$scope`** and **`$element`** are injected to the preloader as the directive
  scope and element, respectively. If `preloader` is the name an expression (&)
  scope binding, `$element` is not injected as this is illegal in AngularJS.
      
- **`nodisable`**  
  Do not disable element on `click` flag.

### $uibModal.open Options

See [**`$uibModal.open`**][1] documentation for remaining modal dialog options.
  
Specific options are set as follows:

| Property | Value |
| :------- | :---- |
| **`scope`**            | Directive controller **`$scope`**. |
| **`bindToController`** | **`true`**. |
| **`controllerAs`**     | Default = "$ctrl". |

# Example

A usage example [can be found here][2].

# Notes

  - Directive scope is **`$parent`** of the dialog controller scope. If the 
    directive "**`scope`**" properties were bound to the directive controller, 
    they would be available to the dialog only through **`$parent.$ctrl`**.
  
    By NOT binding to the directive controller, the "**`scope`**" properties
    are available through **`$parent`**. Binding the directive scope to the 
    dialog controller copies **`$parent`** to **`$ctrl`**.
    
  - There is no linkage between the dialog controller scope and the parent
    of the directive controller scope. Therefore, Two-way (=) "**`scope`**" 
    bindings are not supported.

[1]: https://angular-ui.github.io/bootstrap/#!#modal
[2]: /tests/ui/ClickDialogDirective
*******************************************************************************/
/* @ngInject */ export function clickDialogDirective() {
  return function (opts) {
    // Verify there are no two-way bindings.
    if (
      Object.keys(opts.scope || {}).some(function (p) {
        return opts.scope[p][0] == '=';
      })
    )
      throw 'two-way scope bindings (=) are not supported.';

    // If `preloader` is the name of a scope binding, verify it is one-way or expression binding.
    if (angular.isString(opts.preloader))
      if (angular.isString(opts.scope[opts.preloader]))
        if (opts.scope[opts.preloader].length > 0)
          if ('<&'.indexOf(opts.scope[opts.preloader][0]) < 0)
            throw (
              "'preloader' scope binding '" +
              opts.preloader +
              "' must be a one-way (<) or expression (&) binding."
            );

    // Return new directive properties.
    return {
      restrict: 'A',
      scope: opts.scope || true,
      controller: [
        '$scope',
        '$uibModal',
        '$element',
        '$attrs',
        '$injector',
        '$q',
        function ($scope, $uibModal, $element, $attrs, $injector, $q) {
          $element.bind('click', function () {
            // Return if the element is disabled.
            if ($attrs['disabled']) return false;

            // Set `disabled` attribute in element. `enableClick` will remove it when finished with the dialog.
            var enableClick = angular.noop;

            if (!opts.nodisable) {
              $attrs.$set('disabled', true);
              enableClick = function () {
                $attrs.$set('disabled', false);
              };
            }

            // If `preloader` is a function, use as given. Else, get the function from the scope property.
            var preloader = angular.isFunction(opts.preloader)
              ? opts.preloader
              : angular.isString(opts.preloader)
                ? $scope[opts.preloader]
                : null;

            if (preloader && !angular.isFunction(preloader))
              throw 'preloader is not a function';

            // Execute the preloader injecting `$scope` and `$element`.
            var preloading = (function () {
              if (!preloader) return $q.resolve();
              // Provided as expression binding (&).
              else if (
                angular.isString(opts.preloader) &&
                opts.scope[opts.preloader][0] == '&'
              )
                return $q.resolve(preloader({ $scope: $scope }));
              // Provided as function or one-way binding (<).
              else
                return $q.resolve(
                  $injector.invoke(preloader, null, {
                    $scope: $scope,
                    $element: $element
                  })
                );
            })();

            preloading
              .then(function (preloaded) {
                var modalOpts = Object.assign({ controllerAs: '$ctrl' }, opts, {
                  scope: $scope,
                  bindToController: true
                });
                // Inject preloaded data to controller as `$preloaded`.
                if (preloaded)
                  modalOpts.resolve = Object.assign({}, opts.resolve, {
                    $preloaded: function () {
                      return preloaded;
                    }
                  });

                // Open the dialog and re-enable click handler when closed.
                $uibModal.open(modalOpts).closed.then(enableClick);
              })
              // Re-enable click handler if preloader did not resolve.
              .catch(enableClick);

            // Prevent the default HTML onclick action.
            return false;
          });
        }
      ]
    };
  };
}
